import React from "react";
import { ResponsiveSankey } from "@nivo/sankey";

import {
  emptyString,
  inboxReportColors,
  conversionReportColors,
} from "../../../constants";
import { compactNumberFormat } from "../../utility";
import { reportTypesConstants } from "../../../views/admin-screens/constants";

const chartConstants = {
  VALUE: "value",
  COLOR: "color",
  BLACK: "black",
  INSIDE: "inside",
  SORT_OPTION: "input",
  ORIENTATION: "horizontal",
  ALIGNMENT_CENTER: "center",
  TEXT_ANCHOR_LEFT: "left",
};

const chartLabelName = {
  PASSIVE: "Passive",
  POSITIVE: "Positive",
  NEGATIVE: "Negative",
  SURVEYS_SENT: "Surveys Sent",
  SURVEYS_OPENED: "Surveys Opened",
  SURVEYS_STARTED: "Surveys Started",
  SURVEYS_COMPLETED: "Surveys Completed",
  CONSUMER_RESPONDED: "Responded",
  TOTAL_CONVERSATION_SENT: "Total Conversation Sent",
  TOTAL_CONVERSATION: "Total Conversation",
  CONVERSATION_STARTED: "Started",
  CONVERSATION_NOT_STARTED: "Not Started",
  CONSUMER_NOT_RESPONDED: "Not Responded",
  GOOGLE_REVIEW_REDIRECT: "Google Review Redirect",
};

const colorSchemes = {
  [reportTypesConstants.CONVERSION_REPORT]: conversionReportColors,
  [reportTypesConstants.INBOX_REPORT]: inboxReportColors,
};

const SankeyChart = ({
  surveyConversionRateKpiSummary,
  isGoogleReviewFilterSelected,
  renderItemIfValueExist,
  reportType,
}) => {
  const {
    totalSurveyOpenedCount,
    totalGoogleReviewCount,
    totalSurveyStartedCount,
    totalSurveyCompleteCount,
    totalPassiveSurveyCompleteCount,
    totalPositiveSurveyCompleteCount,
    totalNegativeSurveyCompleteCount,
    totalConversationCount,
    totalConversationNotStartedCount,
    totalConversationStartedCount,
    totalConsumerNotRespondedCount,
    totalConsumerRespondedCount,
  } = surveyConversionRateKpiSummary;

  const data = {
    nodes: [
      { id: chartLabelName.SURVEYS_SENT },
      { id: chartLabelName.SURVEYS_OPENED },
      { id: chartLabelName.SURVEYS_STARTED },
      { id: chartLabelName.SURVEYS_COMPLETED },
      { id: chartLabelName.POSITIVE },
      { id: chartLabelName.PASSIVE },
      { id: chartLabelName.NEGATIVE },
      ...(isGoogleReviewFilterSelected
        ? [{ id: chartLabelName.GOOGLE_REVIEW_REDIRECT }]
        : []),
      { id: chartLabelName.TOTAL_CONVERSATION_SENT },
      { id: chartLabelName.TOTAL_CONVERSATION },
      { id: chartLabelName.CONVERSATION_NOT_STARTED },
      { id: chartLabelName.CONVERSATION_STARTED },
      { id: chartLabelName.CONSUMER_RESPONDED },
      { id: chartLabelName.CONSUMER_NOT_RESPONDED },
    ],
    links: [
      ...(renderItemIfValueExist(totalSurveyOpenedCount)
        ? [
            {
              source: chartLabelName.SURVEYS_SENT,
              target: chartLabelName.SURVEYS_OPENED,
              value: totalSurveyOpenedCount?.value,
            },
          ]
        : []),
      ...(renderItemIfValueExist(totalSurveyStartedCount)
        ? [
            {
              source: chartLabelName.SURVEYS_OPENED,
              target: chartLabelName.SURVEYS_STARTED,
              value: totalSurveyStartedCount?.value,
            },
          ]
        : []),
      ...(renderItemIfValueExist(totalSurveyCompleteCount)
        ? [
            {
              source: chartLabelName.SURVEYS_STARTED,
              target: chartLabelName.SURVEYS_COMPLETED,
              value: totalSurveyCompleteCount?.value,
            },
          ]
        : []),
      ...(renderItemIfValueExist(totalPositiveSurveyCompleteCount)
        ? [
            {
              source: chartLabelName.SURVEYS_COMPLETED,
              target: chartLabelName.POSITIVE,
              value: totalPositiveSurveyCompleteCount?.value,
            },
          ]
        : []),
      ...(renderItemIfValueExist(totalPassiveSurveyCompleteCount)
        ? [
            {
              source: chartLabelName.SURVEYS_COMPLETED,
              target: chartLabelName.PASSIVE,
              value: totalPassiveSurveyCompleteCount?.value,
            },
          ]
        : []),
      ...(renderItemIfValueExist(totalNegativeSurveyCompleteCount)
        ? [
            {
              source: chartLabelName.SURVEYS_COMPLETED,
              target: chartLabelName.NEGATIVE,
              value: totalNegativeSurveyCompleteCount?.value,
            },
          ]
        : []),
      ...(isGoogleReviewFilterSelected &&
      renderItemIfValueExist(totalGoogleReviewCount)
        ? [
            {
              source: chartLabelName.POSITIVE,
              target: chartLabelName.GOOGLE_REVIEW_REDIRECT,
              value: totalGoogleReviewCount?.value,
            },
          ]
        : []),
      ...(renderItemIfValueExist(totalConversationCount)
        ? [
            {
              source: chartLabelName.TOTAL_CONVERSATION_SENT,
              target: chartLabelName.TOTAL_CONVERSATION,
              value: totalConversationCount?.value,
            },
          ]
        : []),
      ...(renderItemIfValueExist(totalConversationNotStartedCount)
        ? [
            {
              source: chartLabelName.TOTAL_CONVERSATION,
              target: chartLabelName.CONVERSATION_NOT_STARTED,
              value: totalConversationNotStartedCount?.value,
            },
          ]
        : []),
      ...(renderItemIfValueExist(totalConversationStartedCount)
        ? [
            {
              source: chartLabelName.TOTAL_CONVERSATION,
              target: chartLabelName.CONVERSATION_STARTED,
              value: totalConversationStartedCount?.value,
            },
          ]
        : []),
      ...(renderItemIfValueExist(totalConsumerRespondedCount)
        ? [
            {
              source: chartLabelName.CONVERSATION_STARTED,
              target: chartLabelName.CONSUMER_RESPONDED,
              value: totalConsumerRespondedCount?.value,
            },
          ]
        : []),
      ...(renderItemIfValueExist(totalConsumerNotRespondedCount)
        ? [
            {
              source: chartLabelName.CONVERSATION_STARTED,
              target: chartLabelName.CONSUMER_NOT_RESPONDED,
              value: totalConsumerNotRespondedCount?.value,
            },
          ]
        : []),
    ],
  };

  const renderNode = (node) => {
    let displayValue = emptyString;
    let displayPercentage = emptyString;
    const isSubbranch =
      node.id === chartLabelName.POSITIVE ||
      node.id === chartLabelName.PASSIVE ||
      node.id === chartLabelName.NEGATIVE ||
      node.id === chartLabelName.CONVERSATION_STARTED ||
      node.id === chartLabelName.CONVERSATION_NOT_STARTED ||
      node.id === chartLabelName.CONSUMER_RESPONDED ||
      node.id === chartLabelName.CONSUMER_NOT_RESPONDED ||
      node.id === chartLabelName.GOOGLE_REVIEW_REDIRECT;

    switch (node.id) {
      case chartLabelName.SURVEYS_SENT:
        if (renderItemIfValueExist(totalSurveyOpenedCount)) {
          displayValue = compactNumberFormat(totalSurveyOpenedCount?.value);
          displayPercentage = totalSurveyOpenedCount?.percentage;
        }
        break;
      case chartLabelName.SURVEYS_OPENED:
        if (renderItemIfValueExist(totalSurveyStartedCount)) {
          displayValue = compactNumberFormat(totalSurveyStartedCount?.value);
          displayPercentage = totalSurveyStartedCount?.percentage;
        }
        break;
      case chartLabelName.SURVEYS_STARTED:
        if (renderItemIfValueExist(totalSurveyCompleteCount)) {
          displayValue = compactNumberFormat(totalSurveyCompleteCount?.value);
          displayPercentage = totalSurveyCompleteCount?.percentage;
        }
        break;
      case chartLabelName.POSITIVE:
        if (renderItemIfValueExist(totalPositiveSurveyCompleteCount)) {
          displayValue = compactNumberFormat(
            totalPositiveSurveyCompleteCount?.value
          );
          displayPercentage = `${chartLabelName.POSITIVE} ${totalPositiveSurveyCompleteCount?.percentage}`;
        }
        break;
      case chartLabelName.PASSIVE:
        if (renderItemIfValueExist(totalPassiveSurveyCompleteCount)) {
          displayValue = compactNumberFormat(
            totalPassiveSurveyCompleteCount?.value
          );
          displayPercentage = `${chartLabelName.PASSIVE} ${totalPassiveSurveyCompleteCount?.percentage}`;
        }
        break;
      case chartLabelName.NEGATIVE:
        if (renderItemIfValueExist(totalNegativeSurveyCompleteCount)) {
          displayValue = compactNumberFormat(
            totalNegativeSurveyCompleteCount?.value
          );
          displayPercentage = `${chartLabelName.NEGATIVE} ${totalNegativeSurveyCompleteCount?.percentage}`;
        }
        break;
      case chartLabelName.GOOGLE_REVIEW_REDIRECT:
        if (renderItemIfValueExist(totalGoogleReviewCount)) {
          displayValue = compactNumberFormat(totalGoogleReviewCount?.value);
          displayPercentage = `${chartLabelName.GOOGLE_REVIEW_REDIRECT} ${totalGoogleReviewCount?.percentage}`;
        }
        break;
      case chartLabelName.TOTAL_CONVERSATION_SENT:
        if (renderItemIfValueExist(totalConversationCount)) {
          displayValue = compactNumberFormat(totalConversationCount?.value);
          displayPercentage = totalConversationCount?.percentage;
        }
        break;
      case chartLabelName.CONVERSATION_STARTED:
        if (renderItemIfValueExist(totalConversationStartedCount)) {
          displayValue = compactNumberFormat(
            totalConversationStartedCount?.value
          );
          displayPercentage = `${chartLabelName.CONVERSATION_STARTED} ${totalConversationStartedCount?.percentage}`;
        }
        break;
      case chartLabelName.CONVERSATION_NOT_STARTED:
        if (renderItemIfValueExist(totalConversationNotStartedCount)) {
          displayValue = compactNumberFormat(
            totalConversationNotStartedCount?.value
          );
          displayPercentage = `${chartLabelName.CONVERSATION_NOT_STARTED} ${totalConversationNotStartedCount?.percentage}`;
        }
        break;
      case chartLabelName.CONSUMER_NOT_RESPONDED:
        if (renderItemIfValueExist(totalConsumerNotRespondedCount)) {
          displayValue = compactNumberFormat(
            totalConsumerNotRespondedCount?.value
          );
          displayPercentage = `${chartLabelName.CONSUMER_NOT_RESPONDED} ${totalConsumerNotRespondedCount?.percentage}`;
        }
        break;
      case chartLabelName.CONSUMER_RESPONDED:
        if (renderItemIfValueExist(totalConsumerRespondedCount)) {
          displayValue = compactNumberFormat(
            totalConsumerRespondedCount?.value
          );
          displayPercentage = `${chartLabelName.CONSUMER_RESPONDED} ${totalConsumerRespondedCount?.percentage}`;
        }
        break;

      default:
        displayValue = emptyString;
        displayPercentage = emptyString;
    }

    return (
      <>
        <tspan
          dy={0}
          dx={isSubbranch ? 10 : 0}
          fontSize={18}
          fontWeight={600}
          textAnchor={chartConstants.TEXT_ANCHOR_LEFT}
        >
          {displayValue}
        </tspan>
        {displayPercentage && (
          <tspan
            dy={isSubbranch ? 0 : 15}
            dx={isSubbranch ? 8 : -27}
            fontSize={12}
            fontWeight={500}
            textAnchor={chartConstants.TEXT_ANCHOR_LEFT}
          >
            {displayPercentage}%
          </tspan>
        )}
      </>
    );
  };

  const renderNodeTooltip = ({ node }) => {
    let tooltipValue;

    switch (node.id) {
      case chartLabelName.CONVERSATION_STARTED:
        tooltipValue = totalConversationStartedCount?.value;
        break;
      case chartLabelName.CONVERSATION_NOT_STARTED:
        tooltipValue = totalConversationNotStartedCount?.value;
        break;
      default:
        tooltipValue = node.value;
        break;
    }

    return (
      <div className="p-2 bg-white rounded shadow">
        <strong>{node.id}:</strong> {compactNumberFormat(tooltipValue)}
      </div>
    );
  };

  const linkTooltip = ({ link }) => {
    return (
      <div className="p-2 bg-white rounded shadow">
        <strong>{link.target.id}:</strong>{" "}
        {compactNumberFormat(link.target.value)}
      </div>
    );
  };

  if (totalSurveyOpenedCount?.value || totalConversationCount?.value) {
    return (
      <div className="amchart-funnel-wrapper">
        <ResponsiveSankey
          data={data}
          colors={colorSchemes[reportType]}
          nodeOpacity={1}
          nodeSpacing={5}
          linkContract={0}
          linkOpacity={0.8}
          labelPadding={16}
          label={renderNode}
          nodeThickness={10}
          enableLinkGradient
          nodeBorderWidth={0}
          linkHoverOpacity={1}
          linkTooltip={linkTooltip}
          value={chartConstants.VALUE}
          nodeTooltip={renderNodeTooltip}
          sort={chartConstants.SORT_OPTION}
          layout={chartConstants.ORIENTATION}
          labelPosition={chartConstants.INSIDE}
          labelTextColor={chartConstants.BLACK}
          align={chartConstants.ALIGNMENT_CENTER}
          nodeColor={{ from: chartConstants.COLOR }}
          labelOrientation={chartConstants.ORIENTATION}
          nodeBorderColor={{ from: chartConstants.COLOR }}
          margin={{ top: 40, right: 30, bottom: 5, left: 30 }}
        />
      </div>
    );
  } else {
    return (
      <div className="amchart-funnel-wrapper d-flex align-items-center justify-content-center fw-bold">
        No data to display
      </div>
    );
  }
};
export default SankeyChart;
